<template>
    <section class="section section-shaped section-lg my-0">
        <div class="shape shape-style-1 bg-gradient-default">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
        </div>
        <div class="container pt-lg-md">
            <div class="row justify-content-center">
              <!-- Content Start -->
              <div class="col-lg-8 col-md-10 mx-auto">
                <h1 class="post-heading">Why I tend to prefer equity rounds over notes</h1>
                <span class="meta"> Posted in December 2021 </span>
                
                <p>All but two of my last 10 investments have taken the form of straight equity. Furthermore, all of the deals in which Shizen Capital was lead investor over the past two years have also been for equity rounds. In this post I will lay out the reasons that I prefer equity rounds to convertible notes or SAFE notes in early stage venture investments.</p>

                <p>For simplicity here, I will use the generic term note to encompass any type of non-equity instrument that is convertible into a startup's equity in the future based on certain conditions. This includes therefore classic convertible notes as well as SAFE and JKISS notes. [Note: there are some key distinctions in the implementation; notably, SAFE and JKISS notes generally behave more like warrants than debt, in that they typically do not carry an interest rate nor a maturity date).</p>

                <p>My preference for investing with equity rather than a note center on two of the guiding principles we hold dear at Shizen Capital when partnering with founders: alignment and transparency.</p>

                <p>First, let’s revisit why notes can seem more alluring than a priced equity round:
                  <ol>
                    <li> they are less costly and more expedient to implement from a legal perspective</li>
                    <li> they sidestep a difficult negotiation over valuation</li>
                    <li> they can surmount a conflict of interest for investors during an internal round</li>
                    <li> they grant investors additional optionality and seniority in the financing of the company</li>
                  </ol>
                </p>
        
                <p>Now let’s discuss these characteristics one by one:</p>

                <p>True, a note agreement is simply a contract between two parties: the investor (as note-holder) and the startup. At a future point, the note converts into equity or is reimbursed, based on conditions defined in the agreement.</p>

                <p>Since no equity is being issued at the time of a note financing, corporate formalities and legal filings are unnecessary. There is no need to update the articles of association, draft a shareholders agreement, or make any formal filings. The investor could even dispense with hiring a lawyer entirely for such a transaction, thus saving fees (the founders could do so as well, though I personally recommend founders seek at least some minimum level of legal counsel). However, once the future hoped-for equity round materializes, all of these aforementioned legal formalities will become necessary.</p>

                <h2 class="section-heading">SAFE notes can be fast but only if the investor moves fast</h2>

                <p>In theory, transactions with notes (again, including SAFE’s and JKISS’s here) are faster to implement then equity rounds. In theory. If handled deftly, a straightforward equity investment should take a few weeks to implement. A note, in contrast, can be implemented within a few days (especially a SAFE or JKISS, which are based on a standard template). However, I find it cringe-worthy all too often to hear founders lament to me about how their fundraising efforts via a note are dragging out for weeks or months. I admittedly have not performed a scientific analysis on this, but anecdotally my observations are that weeks or months of note discussions are not uncommon in many regions outside of Silicon Valley. </p>

                <h2 class="section-heading">Postponing uncomfortable conversations</h2>

                <p>Sidestepping a difficult negotiation on valuation can also be an appealing feature of financing via a note, which does not place a price on the equity of the company at the time of the transaction. If a founder and investor cannot agree on valuation at the time of the fundraising, a note postpones this uncomfortable conversation on price. </p>
                
                <p>The distinction between convertible notes and SAFE notes becomes relevant here. While a convertible note often eliminates any reference to valuation, a SAFE note by its very construction usually contains a valuation cap. This valuation cap does not represent the valuation of the company at the time, but it does require some negotiated consensus between the parties, and it also lays the groundwork for future signaling to the market.</p>

                <h2 class="section-heading">Transparency</h2>

                <p>Furthermore, this is where the principle of transparency comes in. Postponing the uncomfortable valuation conversation is simply kicking the can down the road. Eventually this conversation has to take place, and the stakes will likely be much higher in the future than today. Moreover, numerous other unexpected consequences can arise from this approach. Because I’ve seen this play out across a vast number of companies over the years, often to the detriment of founders, I feel that in the spirit of transparency I have an obligation to alert founders to what I’ve witnessed. [Note: I’ve raised the alarm in detail on this issue <a href="http://rude.vc/SAFE">here</a>. And here is the <a href="http://rude.vc/uXHZ">Japanese version</a> of the same piece].</p>

                <h2 class="section-heading">Internal rounds</h2>

                <p>For most professional VC funds, internal rounds can raise compliance issues if not done properly. For avoidance of doubt, by internal round I mean a future financing round of a startup where no significant external parties invest in the company. A VC fund refinancing one of its existing portfolio companies without an external market participant would be required to justify the subsequent valuation if the new round is priced in equity, reflecting an inherent conflict of interest. Employing a convertible note (often structured as a convertible bridge loan in these instances) can surmount this issue.</p>

                <h2 class="section-heading">Risk of misalignment</h2>

                <p>Lastly, financing via a note naturally grants the investor an additional degree of optionality and potentially even seniority in the fundraising.</p>

                <p>Let's start with the notion of seniority (more flagrant in convertible notes than in SAFE or JKISS notes). From an investor’s perspective, sitting senior to all the shareholders in a company offers the best of both worlds: if things go well, convert and reap the upside; if things don’t go well, redeem for your money back plus interest, even if it throws the company into financial distress. Accordingly, the terms of a convertible note document matter. Founders need to review the fine print before entering into one.</p>

                <p>The notion of optionality is a bit more nuanced. As a VC, I welcome optionality; in fact I actively seek it out for sound portfolio management. However, I want the founders into whom I invest to fully understand the implications of it in the case of notes. Let’s illustrate with a simple example: the VC invests 50M¥ in a seed round via a SAFE note that contains a 20% discount and a 400M¥ valuation cap. When it's time for the Series A, the respective interests of the investor and founder diverge due to a slight misalignment. The founder’s proximate incentive is to boost the valuation of the series A higher, and preferably high enough to neutralize the discount, i.e. above 500M¥. In contrast, the investor's incentive favors a lower valuation, because the lower the valuation of the Series A, the greater the number of shares into which the investor's note will convert. Had the seed round been raised as a priced equity round rather than via a note, both founder and investor would be aligned in the dilution they would face from the future Series A. </p>

                <p>I am not ideologically opposed to investing notes. Here at Shizen Capital we approach every prospective investment as a long-term relationship. Accordingly, we believe that the better we can align incentives and act with transparency with the founders we back, the healthier and more fruitful our collective partnership will be.</p>

                <figure>
                  <img class="img-fluid" src="img/blog/card/equityrounds.png" alt="">
                </figure>

              </div>
            </div>
        </div>
    </section>
</template>
<script>
export default {};
</script>
<style>
.post-heading,
.subheading,
.section-heading,
.blockquote,
.meta,
p,
li,
span {
  color: #fff;
}
.post-heading {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}
.subheading {
  font-size: 20px;
  margin-top: 16px;
}
.section-heading {
  font-weight: 700;
  margin-top: 32px;
}
.section-heading + p {
  margin-top: 16px;
}
.meta + p {
  margin-top: 32px;
}
.author-info {
  font-size: 14px;
  font-style: italic;
  line-height: 1.5;
}
figure {
  margin-top: 24px;
}
</style>
